import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { CheckCircle, Loader2 } from 'lucide-react';
import { getLscBillingStatus } from '../api';
import type { LscBillingStatusResponse } from '../types';
import { isActivePaidAccountStatus } from '../entitlementBillingErrors';
import { isCompedEntitlement, LSC_BILLING_SUCCESS_BODY, LSC_BILLING_SUCCESS_TITLE, LSC_SUBSCRIPTION_FOOTNOTE } from '../billingCopy';
import { clearCheckoutLeagueId, readCheckoutLeagueId } from '../checkoutLeagueContext';
import BillingSupportContact from '../billingSupportContact';
import PaidUnboundRecoveryBanner from '../components/PaidUnboundRecoveryBanner';
import { resolvePaidUnboundRecovery } from '../paidUnboundRecovery';
import { lscPath } from '../routing';
import { buildSetupSubscriptionReturnPath } from '../setupSubscriptionReturn';
import { useLscContentColumn } from '../lscContentColumn';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 8;

type SuccessPhase = 'confirming' | 'active_bound' | 'active_unbound' | 'pending';

function resolveSuccessPhase(status: LscBillingStatusResponse | null): SuccessPhase {
  if (!status) return 'confirming';
  if (isCompedEntitlement(status.entitlement)) return 'active_bound';
  if (status.entitlement) return 'active_bound';
  if (isActivePaidAccountStatus(status)) return 'active_unbound';
  return 'confirming';
}

const LscBillingSuccessPage: React.FC = () => {
  useLscContentColumn('narrow');
  const [searchParams] = useSearchParams();
  const leagueId = useMemo(() => {
    const fromQuery = (searchParams.get('league_id') || '').trim();
    if (fromQuery) return fromQuery;
    return readCheckoutLeagueId();
  }, [searchParams]);
  const [billingStatus, setBillingStatus] = useState<LscBillingStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [pollAttempt, setPollAttempt] = useState(0);

  const loadStatus = useCallback(async () => {
    try {
      const status = await getLscBillingStatus(leagueId);
      setBillingStatus(status);
      return status;
    } catch {
      return null;
    }
  }, [leagueId]);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      setLoading(true);
      const status = await loadStatus();
      if (cancelled) return;
      setLoading(false);
      setPollAttempt(attempts);

      const phase = resolveSuccessPhase(status);
      if (phase !== 'confirming' || attempts >= MAX_POLL_ATTEMPTS) {
        if (phase === 'active_bound' || phase === 'active_unbound') {
          clearCheckoutLeagueId();
        }
        return;
      }

      attempts += 1;
      window.setTimeout(() => {
        if (!cancelled) void poll();
      }, POLL_INTERVAL_MS);
    };

    void poll();

    return () => {
      cancelled = true;
    };
  }, [loadStatus]);

  const phase = resolveSuccessPhase(billingStatus);
  const stillConfirming = phase === 'confirming' && pollAttempt < MAX_POLL_ATTEMPTS;
  const recovery = useMemo(
    () =>
      resolvePaidUnboundRecovery(billingStatus, {
        ownedLeagueIds: leagueId ? [leagueId] : undefined,
      }),
    [billingStatus, leagueId]
  );

  const leagueDashboardPath = leagueId ? lscPath(`/leagues/${leagueId}`) : lscPath('/leagues');
  const leagueSetupPath = buildSetupSubscriptionReturnPath(leagueId);
  const leagueBillingPath = leagueId
    ? lscPath(`/leagues/${leagueId}/settings`)
    : lscPath('/billing');

  return (
    <div className="space-y-6 max-w-2xl" data-testid="lsc-billing-success-page">
      <div className="card text-center space-y-4">
        {stillConfirming || loading ? (
          <Loader2
            className="h-10 w-10 text-primary-400 mx-auto animate-spin"
            aria-hidden
            data-testid="lsc-billing-success-loading"
          />
        ) : (
          <CheckCircle className="h-10 w-10 text-green-400 mx-auto" aria-hidden />
        )}
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">{LSC_BILLING_SUCCESS_TITLE}</h1>
          <p className="text-sm text-secondary-300">{LSC_BILLING_SUCCESS_BODY}</p>
          {stillConfirming && (
            <p className="text-xs text-secondary-500 mt-2" data-testid="lsc-billing-success-pending">
              This may take a moment to appear in billing status.
            </p>
          )}
          {phase === 'active_bound' && (
            <p className="text-sm text-green-200 mt-3" data-testid="lsc-billing-success-bound" role="status">
              Your subscription is linked to your league. Continue setup from your league dashboard.
            </p>
          )}
          {phase === 'confirming' && !stillConfirming && !loading && (
            <p className="text-sm text-secondary-400 mt-3" role="status">
              If your subscription is active but not linked to a league yet, use the steps below.
            </p>
          )}
        </div>
        <p className="text-xs text-secondary-500">{LSC_SUBSCRIPTION_FOOTNOTE}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
          <Link to={lscPath('/billing')} className="btn-primary">
            View billing status
          </Link>
          {phase === 'active_bound' && leagueSetupPath ? (
            <Link
              to={leagueSetupPath}
              className="btn-primary min-h-11 inline-flex items-center justify-center px-4"
              data-testid="lsc-billing-success-continue-setup"
            >
              Continue subscription setup
            </Link>
          ) : null}
          {leagueSetupPath ? (
            <Link
              to={leagueSetupPath}
              className="btn-secondary min-h-11 inline-flex items-center justify-center px-4"
              data-testid="lsc-billing-success-return-setup"
            >
              Return to league setup
            </Link>
          ) : null}
          {leagueId ? (
            <Link to={leagueBillingPath} className="btn-secondary">
              League subscription settings
            </Link>
          ) : (
            <Link to={leagueDashboardPath} className="btn-secondary">
              Go to leagues
            </Link>
          )}
        </div>
      </div>

      {(phase === 'active_unbound' || recovery.show) && (
        <PaidUnboundRecoveryBanner
          recovery={
            recovery.show
              ? recovery
              : {
                  mode: 'unbound',
                  show: true,
                  boundLeagueCount: 0,
                  canQuickBind: false,
                }
          }
          settingsLeagueId={leagueId || undefined}
          onBound={() => void loadStatus()}
        />
      )}

      <BillingSupportContact className="text-sm text-secondary-500 text-center" />
    </div>
  );
};

export default LscBillingSuccessPage;

export { resolveSuccessPhase, MAX_POLL_ATTEMPTS, POLL_INTERVAL_MS };
